import { useEffect, useState } from "react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

import { BookMarked, Plus, RefreshCw, Trash2, Sparkles, PencilRuler } from "lucide-react";

type Pattern = {
  id: string;
  kind: "learned" | "manual";
  category: string;
  signal_key: string;
  title: string;
  description: string;
  cadence: string | null;
  cadence_detail: Record<string, unknown>;
  evidence: Array<{ at: string; note: string }>;
  occurrence_count: number;
  confidence: number;
  first_observed_at: string | null;
  last_observed_at: string | null;
  enabled: boolean;
  notes: string | null;
  created_at: string;
};

const CATEGORIES = [
  "payroll", "marketing", "communications", "onboarding",
  "photos", "open_house", "enrollment", "operations", "other",
];

function ConfidenceBar({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const tone = pct >= 75 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-muted-foreground/60";
  return (
    <div className="flex items-center gap-2 min-w-[110px]">
      <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-muted-foreground w-9 text-right">{pct}%</span>
    </div>
  );
}

export default function PlaybookPage() {
  const [patterns, setPatterns] = useState<Pattern[]>([]);
  const [loading, setLoading] = useState(true);
  const [learning, setLearning] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "operations", notes: "" });

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("eos_playbook_patterns")
      .select("*")
      .order("kind", { ascending: false }) // manual first
      .order("confidence", { ascending: false });
    if (error) {
      toast({ title: "Could not load the playbook", description: "Something went wrong. Try again in a moment.", variant: "destructive" });
    } else {
      setPatterns((data ?? []) as unknown as Pattern[]);
    }
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function runLearner() {
    setLearning(true);
    try {
      const { error } = await supabase.functions.invoke("playbook-learner", { body: {} });
      if (error) throw error;
      toast({ title: "MAVIS finished reviewing recent work", description: "Any new habits are ready for your review." });
      await load();
    } catch (e) {
      toast({ title: "MAVIS couldn't finish learning right now", description: "Something went wrong. Try again in a moment.", variant: "destructive" });
    }
    setLearning(false);
  }

  async function togglePattern(p: Pattern, enabled: boolean) {
    const { error } = await supabase.from("eos_playbook_patterns").update({ enabled }).eq("id", p.id);
    if (error) return toast({ title: "Couldn't save that change", description: "Something went wrong. Try again in a moment.", variant: "destructive" });
    setPatterns((prev) => prev.map((x) => x.id === p.id ? { ...x, enabled } : x));
  }

  async function deletePattern(p: Pattern) {
    if (!confirm(`Delete "${p.title}"? MAVIS will stop using this pattern.`)) return;
    const { error } = await supabase.from("eos_playbook_patterns").delete().eq("id", p.id);
    if (error) return toast({ title: "Couldn't remove that pattern", description: "Something went wrong. Try again in a moment.", variant: "destructive" });
    setPatterns((prev) => prev.filter((x) => x.id !== p.id));
    toast({ title: "Removed from the playbook" });
  }

  async function addManualRule() {
    if (!form.title.trim() || !form.description.trim()) {
      return toast({ title: "Add a short title and description first", variant: "destructive" });
    }
    const signal_key = `manual.${form.category}.${form.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}.${Date.now()}`;
    const { error } = await supabase.from("eos_playbook_patterns").insert({
      kind: "manual",
      category: form.category,
      signal_key,
      title: form.title.trim(),
      description: form.description.trim(),
      cadence: "custom",
      confidence: 1.0,
      occurrence_count: 0,
      enabled: true,
      notes: form.notes.trim() || null,
    });
    if (error) return toast({ title: "Couldn't save that rule", description: "Something went wrong. Try again in a moment.", variant: "destructive" });
    setAddOpen(false);
    setForm({ title: "", description: "", category: "operations", notes: "" });
    await load();
    toast({ title: "Rule added to the playbook" });
  }

  const learned = patterns.filter((p) => p.kind === "learned");
  const manual = patterns.filter((p) => p.kind === "manual");

  return (
    <AdminShell>
      <div className="space-y-6">
        {/* Orientation */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <BookMarked className="h-4 w-4" />
              <span>Studio · Executive Playbook</span>
            </div>
            <h1 className="text-2xl font-serif mt-1">Your organization's operating handbook</h1>
            <p className="text-muted-foreground max-w-2xl mt-2 text-sm">
              This is not AI settings — it's the way Silver Sage actually runs. MAVIS learns habits only after seeing
              them at least three times, and always shows you the evidence. You can enable, disable, or delete any
              habit, and add permanent rules of your own.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={runLearner} disabled={learning}>
              <RefreshCw className={`h-4 w-4 mr-2 ${learning ? "animate-spin" : ""}`} />
              {learning ? "Reviewing recent work…" : "Have MAVIS review recent work"}
            </Button>
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button><Plus className="h-4 w-4 mr-2" />Add a rule</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add a permanent operating rule</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Rule title</Label>
                    <Input placeholder="e.g. Marketing posts go out on Tuesday mornings"
                      value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                  </div>
                  <div>
                    <Label>What MAVIS should understand</Label>
                    <Textarea rows={3} placeholder="Explain the rule in your own words. MAVIS will use this to guide recommendations."
                      value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                  </div>
                  <div>
                    <Label>Area</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replace("_", " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Notes (optional)</Label>
                    <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
                  <Button onClick={addManualRule}>Save rule</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Manual rules */}
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <PencilRuler className="h-4 w-4" /> Rules you've set
          </h2>
          {manual.length === 0 ? (
            <Card><CardContent className="py-6 text-sm text-muted-foreground">
              You haven't added any permanent rules yet. Use "Add a rule" for anything you always want MAVIS to follow.
            </CardContent></Card>
          ) : manual.map((p) => (
            <PatternCard key={p.id} p={p} onToggle={togglePattern} onDelete={deletePattern} />
          ))}
        </section>

        {/* Learned patterns */}
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> Habits MAVIS has learned from real work
          </h2>
          {loading ? (
            <Card><CardContent className="py-6 text-sm text-muted-foreground">Loading the playbook…</CardContent></Card>
          ) : learned.length === 0 ? (
            <Card><CardContent className="py-6 text-sm text-muted-foreground">
              MAVIS hasn't observed any recurring habits yet. Once the same action happens at least three times,
              it will show up here with its evidence.
            </CardContent></Card>
          ) : learned.map((p) => (
            <PatternCard key={p.id} p={p} onToggle={togglePattern} onDelete={deletePattern} />
          ))}
        </section>
      </div>
    </AdminShell>
  );
}

function PatternCard({
  p, onToggle, onDelete,
}: {
  p: Pattern;
  onToggle: (p: Pattern, v: boolean) => void;
  onDelete: (p: Pattern) => void;
}) {
  return (
    <Card className={p.enabled ? "" : "opacity-60"}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline">{p.category.replace("_", " ")}</Badge>
              {p.kind === "manual" && <Badge>Manual rule</Badge>}
              {p.cadence && p.cadence !== "custom" && (
                <Badge variant="secondary" className="capitalize">{p.cadence}</Badge>
              )}
            </div>
            <CardTitle className="text-base mt-2 font-serif">{p.title}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">{p.description}</p>
          </div>
          <div className="flex items-center gap-3">
            {p.kind === "learned" && <ConfidenceBar value={p.confidence} />}
            <Switch checked={p.enabled} onCheckedChange={(v) => onToggle(p, v)} aria-label="Enable pattern" />
            <Button variant="ghost" size="icon" onClick={() => onDelete(p)} aria-label="Delete pattern">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
          {p.occurrence_count > 0 && <span>Observed {p.occurrence_count}×</span>}
          {p.last_observed_at && <span>Last seen {new Date(p.last_observed_at).toLocaleDateString()}</span>}
          {p.first_observed_at && <span>First seen {new Date(p.first_observed_at).toLocaleDateString()}</span>}
        </div>
        {p.evidence?.length > 0 && (
          <Accordion type="single" collapsible className="mt-2">
            <AccordionItem value="evidence" className="border-none">
              <AccordionTrigger className="py-2 text-xs text-muted-foreground hover:no-underline">
                Show evidence ({p.evidence.length})
              </AccordionTrigger>
              <AccordionContent>
                <ul className="text-xs space-y-1 text-muted-foreground">
                  {p.evidence.map((e, i) => (
                    <li key={i}>• {new Date(e.at).toLocaleDateString()} — {e.note}</li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
        {p.notes && <p className="text-xs text-muted-foreground mt-2 italic">Note: {p.notes}</p>}
      </CardContent>
    </Card>
  );
}
